const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const COMPILED_VERSION = process.env.REACT_APP_VERSION || '0.0.0';

let intervalId = null;

/**
 * Fetch /version.json (cache-busted) and compare against the compiled-in version.
 * Returns { needsUpdate, remote } or null on failure.
 */
export async function checkForUpdate() {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const remote = await response.json();
    const needsUpdate = remote.version && remote.version !== COMPILED_VERSION;
    return { needsUpdate, remote };
  } catch {
    // Network error or offline — nothing to do
    return null;
  }
}

/**
 * Force-reload the app: unregister SW, clear all caches, then hard-reload.
 */
export async function forceReload() {
  try {
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }

    // Clear all Cache Storage entries
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (e) {
    console.warn('Error clearing caches:', e);
  }

  // Navigate with a cache-busting query param instead of reload():
  // iOS Safari can serve reload() from the HTTP/memory cache even after the
  // SW caches are gone, which made Force Reload look like a no-op. A changed
  // URL always misses the cache; startVersionChecker strips the param again.
  const url = new URL(window.location.href);
  url.searchParams.set('_r', Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Check and auto-reload if a newer version is deployed.
 */
async function checkAndReload() {
  const result = await checkForUpdate();
  if (result && result.needsUpdate) {
    // Loop guard: if we already auto-reloaded for this exact remote version
    // and STILL mismatch, something upstream is serving a stale bundle —
    // reloading again would just spin. Try once per remote version.
    const attempted = sessionStorage.getItem('av_update_attempted');
    if (attempted === result.remote.version) {
      console.warn(
        `Still on ${COMPILED_VERSION} after reloading for ${result.remote.version}; not reloading again this session.`
      );
      return;
    }
    sessionStorage.setItem('av_update_attempted', result.remote.version);
    console.log(
      `Version mismatch: running ${COMPILED_VERSION}, server has ${result.remote.version}. Reloading.`
    );
    await forceReload();
  }
}

/**
 * Start periodic version checking.
 * Also listens for visibilitychange (critical for iOS PWA returning from background).
 */
export function startVersionChecker() {
  // Clean up the cache-buster param a forceReload navigation leaves behind.
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_r')) {
      url.searchParams.delete('_r');
      window.history.replaceState(null, '', url.toString());
    }
  } catch { /* URL API unavailable — cosmetic only */ }

  // Initial check shortly after mount (delay to avoid slowing startup)
  setTimeout(checkAndReload, 10_000);

  // Periodic check every 5 minutes
  intervalId = setInterval(checkAndReload, CHECK_INTERVAL_MS);

  // Check when the user returns to the app (iOS PWA resume)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkAndReload();
    }
  });
}

/**
 * Stop periodic version checking.
 */
export function stopVersionChecker() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

export function getCompiledVersion() {
  return COMPILED_VERSION;
}

export function getBuildTimestamp() {
  return process.env.REACT_APP_BUILD_TIME || null;
}
